/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { SDKCore } from "../core.js";
import {
  appendForm,
  encodeFormQuery,
  encodeSimple,
  normalizeBlob,
} from "../lib/encodings.js";
import {
  bytesToBlob,
  getContentTypeFromFileName,
  readableStreamToArrayBuffer,
} from "../lib/files.js";
import { matchStatusCode } from "../lib/http.js";
import * as M from "../lib/matchers.js";
import { compactMap } from "../lib/primitives.js";
import { safeParse } from "../lib/schemas.js";
import { RequestOptions } from "../lib/sdks.js";
import { extractSecurity, resolveGlobalSecurity } from "../lib/security.js";
import { pathToFunc } from "../lib/url.js";
import * as components from "../models/components/index.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKError } from "../models/errors/sdkerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import * as operations from "../models/operations/index.js";
import { APICall, APIPromise } from "../types/async.js";
import { isBlobLike } from "../types/blobs.js";
import { Result } from "../types/fp.js";
import { isReadableStream } from "../types/streams.js";

/**
 * Speech to text transcription
 *
 * @remarks
 * Convert audio to text using advanced speech recognition.
 *
 * **Complete File Upload (Standard)**
 * Use `Content-Type: multipart/form-data` to upload the complete audio file in one request. Maximum file size: 25MB.
 *
 * Example:
 * ```bash
 * curl -X POST "http://localhost:3000/api/v1/llm/speech/transcriptions?language=en" \
 *   -F "file=@audio.flac"
 * ```
 *
 * **Chunked Upload (Streaming)**
 * Use `Transfer-Encoding: chunked` header to stream audio data in chunks as it's being recorded. No need to know total file size upfront. Server buffers chunks until complete before processing. Maximum total size: 25MB.
 *
 * Example:
 * ```bash
 * curl -X POST "http://localhost:3000/api/v1/llm/speech/transcriptions?language=en" \
 *   -H "Transfer-Encoding: chunked" \
 *   -H "Content-Type: multipart/form-data" \
 *   --data-binary @audio.flac
 * ```
 *
 * **Supported Formats:** FLAC, MP3, MP4, MPEG, MPGA, M4A, OGG, WAV, WebM
 *
 * **Query Parameters:**
 * - `model` (optional): Transcription model identifier. Defaults to 'auto'.
 * - `language` (optional): ISO-639-1 or BCP-47 language code (e.g., "en", "en-US"). Auto-detects if not specified.
 * - `prompt` (optional): Legacy prompt parameter retained for backward compatibility.
 * - `temperature` (optional): Legacy temperature parameter retained for backward compatibility.
 * - `include_speaker_data` (optional): When `true`, include speaker diarization data and require WAV/PCM input. Otherwise transcription uses the standard compatibility path.
 *
 * **Response:** Returns transcribed text in JSON format.
 */
export function llmSpeechTranscribe(
  client: SDKCore,
  request: operations.TranscribeRequest,
  options?: RequestOptions,
): APIPromise<
  Result<
    components.TranscriptionResponse,
    | SDKError
    | ResponseValidationError
    | ConnectionError
    | RequestAbortedError
    | RequestTimeoutError
    | InvalidRequestError
    | UnexpectedClientError
    | SDKValidationError
  >
> {
  return new APIPromise($do(
    client,
    request,
    options,
  ));
}

async function $do(
  client: SDKCore,
  request: operations.TranscribeRequest,
  options?: RequestOptions,
): Promise<
  [
    Result<
      components.TranscriptionResponse,
      | SDKError
      | ResponseValidationError
      | ConnectionError
      | RequestAbortedError
      | RequestTimeoutError
      | InvalidRequestError
      | UnexpectedClientError
      | SDKValidationError
    >,
    APICall,
  ]
> {
  const parsed = safeParse(
    request,
    (value) => operations.TranscribeRequest$outboundSchema.parse(value),
    "Input validation failed",
  );
  if (!parsed.ok) {
    return [parsed, { status: "invalid" }];
  }
  const payload = parsed.value;
  const body = new FormData();

  if (isBlobLike(payload.RequestBody.file)) {
    const file = payload.RequestBody.file;
    const blob = await normalizeBlob(file);
    const name = "name" in file ? (file.name as string) : undefined;
    appendForm(body, "file", blob, name);
  } else if (isReadableStream(payload.RequestBody.file.content)) {
    const buffer = await readableStreamToArrayBuffer(
      payload.RequestBody.file.content,
    );
    const contentType =
      getContentTypeFromFileName(payload.RequestBody.file.fileName)
      || "application/octet-stream";
    appendForm(
      body,
      "file",
      bytesToBlob(buffer, contentType),
      payload.RequestBody.file.fileName,
    );
  } else {
    const contentType =
      getContentTypeFromFileName(payload.RequestBody.file.fileName)
      || "application/octet-stream";
    appendForm(
      body,
      "file",
      bytesToBlob(payload.RequestBody.file.content, contentType),
      payload.RequestBody.file.fileName,
    );
  }

  const path = pathToFunc("/api/v1/llm/speech/transcriptions")();

  const query = encodeFormQuery({
    "include_speaker_data": payload.include_speaker_data,
    "language": payload.language,
    "model": payload.model,
    "prompt": payload.prompt,
    "store": payload.store,
    "temperature": payload.temperature,
  });

  const headers = new Headers(compactMap({
    Accept: "application/json",
    "X-On-Behalf-Of": encodeSimple(
      "X-On-Behalf-Of",
      payload["X-On-Behalf-Of"],
      { explode: false, charEncoding: "none" },
    ),
  }));

  const secConfig = await extractSecurity(client._options.bearerAuth);
  const securityInput = secConfig == null ? {} : { bearerAuth: secConfig };
  const requestSecurity = resolveGlobalSecurity(securityInput);

  const context = {
    options: client._options,
    baseURL: options?.serverURL ?? client._baseURL ?? "",
    operationID: "transcribe",
    oAuth2Scopes: null,

    resolvedSecurity: requestSecurity,

    securitySource: client._options.bearerAuth,
    retryConfig: options?.retries
      || client._options.retryConfig
      || {
        strategy: "backoff",
        backoff: {
          initialInterval: 500,
          maxInterval: 5000,
          exponent: 1.5,
          maxElapsedTime: 15000,
        },
        retryConnectionErrors: true,
      }
      || { strategy: "none" },
    retryCodes: options?.retryCodes || ["429", "500", "502", "503", "504"],
  };

  const requestRes = client._createRequest(context, {
    security: requestSecurity,
    method: "POST",
    baseURL: options?.serverURL,
    path: path,
    headers: headers,
    query: query,
    body: body,
    userAgent: client._options.userAgent,
    timeoutMs: options?.timeoutMs || client._options.timeoutMs || -1,
  }, options);
  if (!requestRes.ok) {
    return [requestRes, { status: "invalid" }];
  }
  const req = requestRes.value;

  const doResult = await client._do(req, {
    context,
    isErrorStatusCode: (statusCode: number) =>
      matchStatusCode({ status: statusCode } as Response, ["4XX", "5XX"]),
    retryConfig: context.retryConfig,
    retryCodes: context.retryCodes,
  });
  if (!doResult.ok) {
    return [doResult, { status: "request-error", request: req }];
  }
  const response = doResult.value;

  const [result] = await M.match<
    components.TranscriptionResponse,
    | SDKError
    | ResponseValidationError
    | ConnectionError
    | RequestAbortedError
    | RequestTimeoutError
    | InvalidRequestError
    | UnexpectedClientError
    | SDKValidationError
  >(
    M.json(200, components.TranscriptionResponse$inboundSchema),
    M.fail("4XX"),
    M.fail("5XX"),
  )(response, req);
  if (!result.ok) {
    return [result, { status: "complete", request: req, response }];
  }

  return [result, { status: "complete", request: req, response }];
}
